import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { 
  User, 
  BarChart3, 
  Target, 
  Users, 
  Settings, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PublicDISCAssessment = () => {
  const { token } = useParams();
  
  // States
  const [linkValid, setLinkValid] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Assessment states
  const [participantName, setParticipantName] = useState('');
  const [participantEmail, setParticipantEmail] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);

  // Likert scale options
  const likertOptions = [
    { value: 1, label: 'Discordo Totalmente' },
    { value: 2, label: 'Discordo' },
    { value: 3, label: 'Neutro' },
    { value: 4, label: 'Concordo' },
    { value: 5, label: 'Concordo Totalmente' }
  ];

  useEffect(() => {
    verifyLink();
  }, [token]);

  const verifyLink = async () => {
    try {
      const response = await axios.get(`${API}/api/disc/public/${token}`);
      setLinkData(response.data);
      setLinkValid(true);
      await loadQuestions();
    } catch (error) {
      console.error('Error verifying link:', error);
      setLinkValid(false);
      if (error.response?.status === 410) {
        toast.error('Este link expirou ou não está mais ativo');
      } else {
        toast.error('Link inválido');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await axios.get(`${API}/api/disc/public/${token}/questions`);
      setQuestions(response.data.questions);
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error(error.response?.data?.detail || 'Erro ao carregar perguntas');
    }
  };

  const startAssessment = () => {
    if (!participantName.trim()) {
      toast.error('Por favor, informe seu nome');
      return;
    }
    
    setAssessmentStarted(true);
    setCurrentQuestionIndex(0);
    setAnswers({});
  };

  const handleAnswerChange = (questionId, score) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: parseInt(score)
    }));
  };

  const nextQuestion = () => {
    const currentQuestion = questions[currentQuestionIndex];
    if (!answers[currentQuestion.id]) {
      toast.error('Por favor, selecione uma resposta');
      return;
    }
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      submitAssessment();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const submitAssessment = async () => {
    setLoading(true);
    
    try {
      const assessmentAnswers = Object.entries(answers).map(([questionId, score]) => ({
        question_id: questionId,
        score: score
      }));

      const assessmentData = {
        participant_name: participantName,
        participant_email: participantEmail,
        answers: assessmentAnswers
      };

      const response = await axios.post(`${API}/api/disc/public/${token}/submit`, assessmentData);
      
      setAssessmentResult(response.data);
      setAssessmentCompleted(true);
      toast.success('Avaliação DISC concluída com sucesso!');
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error('Erro ao processar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const getStyleName = (style) => {
    const names = {
      'D': 'Dominante',
      'I': 'Influente',
      'S': 'Estável', 
      'C': 'Consciente'
    };
    return names[style] || style;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando avaliação...</p>
        </div>
      </div>
    );
  }

  if (!linkValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Inválido</h2>
            <p className="text-gray-600">
              Este link para avaliação DISC não é válido ou já expirou.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Avaliação DISC</h1>
          <p className="text-gray-600">
            Metodologia William Moulton Marston - Análise de Perfil Comportamental
          </p>
          {linkData && (
            <p className="text-sm text-blue-600 mt-2">
              {linkData.description}
            </p>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            {!assessmentStarted && !assessmentCompleted && (
              <div className="space-y-6">
                <div className="text-center space-y-4">
                  <User className="h-16 w-16 text-blue-600 mx-auto" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      Bem-vindo à Avaliação DISC
                    </h2>
                    <p className="text-gray-600">
                      Esta avaliação irá ajudar a identificar seu perfil comportamental através de 48 perguntas.
                    </p>
                  </div>
                </div>

                <div className="max-w-md mx-auto space-y-4">
                  <div>
                    <Label htmlFor="participant_name">Seu Nome *</Label>
                    <Input
                      id="participant_name"
                      value={participantName}
                      onChange={(e) => setParticipantName(e.target.value.replace(/\d/g, ''))}
                      placeholder="Digite seu nome completo"
                    />
                  </div>

                  <div>
                    <Label htmlFor="participant_email">E-mail (Opcional)</Label>
                    <Input
                      id="participant_email"
                      type="email"
                      value={participantEmail}
                      onChange={(e) => setParticipantEmail(e.target.value)}
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                  <h3 className="font-medium text-blue-900 mb-2">Sobre esta avaliação:</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>48 perguntas</strong> baseadas na metodologia Marston</li>
                    <li>• <strong>Escala de 1 a 5</strong> para cada resposta</li>
                    <li>• <strong>Tempo estimado:</strong> 15-20 minutos</li>
                    <li>• <strong>Relatório personalizado</strong> ao final</li>
                  </ul>
                </div>

                <div className="text-center">
                  <Button onClick={startAssessment} size="lg" className="px-8">
                    Iniciar Avaliação
                  </Button>
                </div>
              </div>
            )}

            {assessmentStarted && !assessmentCompleted && questions.length > 0 && (
              <div className="space-y-6">
                {/* Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Progresso</span>
                    <span>{currentQuestionIndex + 1} de {questions.length}</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>

                {/* Question */}
                <Card className="bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">
                          Pergunta {currentQuestionIndex + 1}
                        </Badge>
                        <Badge className={`${
                          questions[currentQuestionIndex].category === 'D' ? 'bg-red-100 text-red-800' :
                          questions[currentQuestionIndex].category === 'I' ? 'bg-yellow-100 text-yellow-800' :
                          questions[currentQuestionIndex].category === 'S' ? 'bg-green-100 text-green-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {getStyleName(questions[currentQuestionIndex].category)}
                        </Badge>
                      </div>
                      
                      <h3 className="text-lg font-medium text-gray-900">
                        {questions[currentQuestionIndex].question}
                      </h3>

                      <RadioGroup
                        value={answers[questions[currentQuestionIndex].id]?.toString() || ''}
                        onValueChange={(value) => handleAnswerChange(questions[currentQuestionIndex].id, value)}
                      >
                        {likertOptions.map(option => (
                          <div key={option.value} className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100">
                            <RadioGroupItem value={option.value.toString()} id={`option-${option.value}`} />
                            <Label htmlFor={`option-${option.value}`} className="flex-grow cursor-pointer">
                              <span className="font-medium">{option.value}</span> - {option.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </CardContent>
                </Card>

                {/* Navigation */}
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={previousQuestion}
                    disabled={currentQuestionIndex === 0}
                  >
                    Anterior
                  </Button>
                  
                  <Button
                    onClick={nextQuestion}
                    disabled={!answers[questions[currentQuestionIndex].id] || loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Processando...
                      </>
                    ) : (
                      currentQuestionIndex === questions.length - 1 ? 'Finalizar' : 'Próxima'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {assessmentCompleted && assessmentResult && (
              <div className="text-center space-y-6">
                <div className="max-w-md mx-auto">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Avaliação Concluída!
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Sua avaliação DISC foi processada com sucesso. Veja abaixo seu perfil comportamental.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Seu Perfil DISC</h4>
                  <div className="flex justify-center items-center space-x-4">
                    <div className="text-center">
                      <Badge className="bg-blue-100 text-blue-800 text-lg px-4 py-2">
                        {assessmentResult.primary_style} - {getStyleName(assessmentResult.primary_style)}
                      </Badge>
                      <p className="text-sm text-gray-600 mt-2">Perfil Principal</p>
                    </div>
                    {assessmentResult.secondary_style && (
                      <div className="text-center">
                        <Badge variant="outline" className="text-lg px-4 py-2">
                          {assessmentResult.secondary_style} - {getStyleName(assessmentResult.secondary_style)}
                        </Badge>
                        <p className="text-sm text-gray-600 mt-2">Perfil Secundário</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="font-medium text-gray-900">Pontuações por Categoria:</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { style: 'D', name: 'Dominante', score: assessmentResult.scores.D, color: '#ef4444' },
                      { style: 'I', name: 'Influente', score: assessmentResult.scores.I, color: '#f59e0b' },
                      { style: 'S', name: 'Estável', score: assessmentResult.scores.S, color: '#10b981' },
                      { style: 'C', name: 'Consciente', score: assessmentResult.scores.C, color: '#3b82f6' }
                    ].map(item => (
                      <div key={item.style} className="text-center p-4 bg-white rounded-lg border">
                        <div className="text-2xl font-bold mb-1" style={{ color: item.color }}>
                          {item.score.toFixed(0)}%
                        </div>
                        <div className="text-sm font-medium text-gray-700">{item.name}</div>
                        <div className="text-xs text-gray-500">({item.style})</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-sm text-gray-600 bg-gray-50 rounded p-4">
                  <p>
                    <strong>Próximos passos:</strong> Para um relatório mais detalhado com recomendações personalizadas, 
                    entre em contato com a empresa que enviou este link.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicDISCAssessment;